import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDeveloperContext } from '@/hooks/useDeveloperContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { UserPlus, Loader2 } from 'lucide-react';

const ROLE_LABELS: Record<string, string> = {
  developer_admin: 'Admin',
  developer_sales_agent: 'Sales Agent',
  developer_sales_lead: 'Sales Team Leader',
  developer_project_manager: 'Project Manager',
  developer_finance: 'Finance',
};

export default function DeveloperTeam() {
  const { ctx, isLoading } = useDeveloperContext();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('developer_sales_agent');

  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ['dev-team-members', ctx.developerId],
    enabled: !!ctx.developerId,
    queryFn: async () => {
      const { data } = await supabase
        .from('developer_team_members')
        .select('id, user_id, member_role, is_active, joined_at')
        .eq('developer_id', ctx.developerId!)
        .order('joined_at', { ascending: false });
      const ids = (data || []).map(d => d.user_id);
      if (!ids.length) return [];
      const { data: profs } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, phone')
        .in('user_id', ids);
      const byId = new Map((profs || []).map(p => [p.user_id, p]));
      return (data || []).map(m => ({ ...m, profile: byId.get(m.user_id) }));
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async () => {
      // Look up user by email via profiles -> auth.users (we use profiles.email if present)
      const { data: prof } = await supabase
        .from('profiles')
        .select('user_id')
        .ilike('email', email.trim())
        .maybeSingle();
      if (!prof?.user_id) throw new Error('No registered user with that email. Ask them to sign up first.');
      const { error } = await supabase.from('developer_team_members').insert({
        developer_id: ctx.developerId,
        user_id: prof.user_id,
        member_role: role as any,
        invited_by: (await supabase.auth.getUser()).data.user?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Member added' });
      setOpen(false); setEmail(''); setRole('developer_sales_agent');
      qc.invalidateQueries({ queryKey: ['dev-team-members'] });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Failed', description: e.message }),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from('developer_team_members').update({ is_active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dev-team-members'] }),
  });

  const updateRole = useMutation({
    mutationFn: async ({ id, member_role }: { id: string; member_role: string }) => {
      const { error } = await supabase.from('developer_team_members').update({ member_role: member_role as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { toast({ title: 'Role updated' }); qc.invalidateQueries({ queryKey: ['dev-team-members'] }); },
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!ctx.developerId) return <div className="text-sm text-muted-foreground">No developer profile linked.</div>;
  if (!ctx.isAdmin) return <div className="text-sm text-muted-foreground">Only the developer admin can manage team members.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Team Members</h2>
          <p className="text-sm text-muted-foreground">{ctx.developerName} · {members.length} member(s)</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><UserPlus className="h-4 w-4 mr-2" />Add Member</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Member email (must be a registered user)</Label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="member@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'developer_admin').map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => inviteMutation.mutate()} disabled={!email || inviteMutation.isPending}>
                {inviteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Members</CardTitle></CardHeader>
        <CardContent>
          {loadingMembers ? (
            <div className="text-sm text-muted-foreground">Loading members…</div>
          ) : members.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No members yet. Invite one above.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Member</TableHead><TableHead>Role</TableHead><TableHead>Joined</TableHead><TableHead>Active</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m: any) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <div className="font-medium text-sm">{m.profile?.full_name || m.user_id.slice(0, 8)}</div>
                      <div className="text-xs text-muted-foreground">{m.profile?.phone || '—'}</div>
                    </TableCell>
                    <TableCell>
                      <Select value={m.member_role} onValueChange={(v) => updateRole.mutate({ id: m.id, member_role: v })}>
                        <SelectTrigger className="h-8 w-[170px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'developer_admin').map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(m.joined_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Switch checked={m.is_active} onCheckedChange={(v) => toggleActive.mutate({ id: m.id, is_active: v })} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
